import React, { Component } from 'react';
import { fromJS } from 'immutable';
import findIndex from 'lodash/findIndex';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';
import A11yFontIcon from 'components/A11yFontIcon';
import FadeElement from 'components/FadeElement';
import MessageForm, { MESSAGE_TYPE_COMMENT } from 'components/MessageForm';
import TextButton from 'components/TextButton';
import { userShape, wallCommentShape, wallPostShape } from 'utils/shapes';
import Comment from './Comment';
import './CommentList.css';

const CHUNK_SIZE = 5;

class CommentList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      commentsShown: this.getMinimumShown(),
      commentsToRemove: [],
      message: '',
      submitting: false,
      showEmojiPicker: false,
      activeCommentSelected: null
    };

    this.showMore = this.showMore.bind(this);
    this.showLess = this.showLess.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.isNewComment = this.isNewComment.bind(this);
    this.isCommentRemoving = this.isCommentRemoving.bind(this);
    this.beginEditComment = this.beginEditComment.bind(this);
    this.cancelEditComment = this.cancelEditComment.bind(this);
    this.saveEditComment = this.saveEditComment.bind(this);
    this.beginDeleteComment = this.beginDeleteComment.bind(this);
    this.deleteComment = this.deleteComment.bind(this);
    this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
    this.addEmoji = this.addEmoji.bind(this);
    this.setActiveCommentSelected = this.setActiveCommentSelected.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.commentInputShown !== prevProps.post.id &&
      this.props.commentInputShown === this.props.post.id &&
      this.state.commentsShown <= this.getMinimumShown() &&
      this.showMoreAvailable()
    ) {
      // When the comment input is opened, automatically show the comments
      this.showMore();
    }
  }

  handleChange(event) {
    this.setState({
      message: event.target.value
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' && event.shiftKey === false) {
      this.handleSubmit(event);
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.props.addComment(this.props.post.id, this.state.message)) {
      this.setState({ message: '', submitting: true });
    }
  }

  getMinimumShown() {
    return this.props.post && this.props.post.isPinned ? 0 : 1;
  }

  getLoadedCommentCount() {
    const { post } = this.props;

    return (
      (post &&
        post.comments &&
        post.comments.comments &&
        post.comments.comments.length) ||
      0
    );
  }

  getTotalCommentCount() {
    return this.props.post.commentCount;
  }

  showMoreAvailable() {
    return this.state.commentsShown < this.getTotalCommentCount();
  }

  showMore() {
    const { post } = this.props;
    const canLoadMore = post && post.comments && post.comments.hasAnyNext;
    const shown = this.state.commentsShown;
    const loaded = this.getLoadedCommentCount();
    const available = Math.max(loaded - shown, 0);

    if (available >= CHUNK_SIZE || !canLoadMore) {
      this.setState({ commentsShown: Math.min(shown + CHUNK_SIZE, loaded) });
    } else if (canLoadMore) {
      this.props.loadMoreComments(post, CHUNK_SIZE, added => {
        const commentsShown = shown + Math.min(added + available, CHUNK_SIZE);
        this.setState({ commentsShown });
      });
    }
  }

  isNewComment(comment) {
    return (
      findIndex(this.props.commentsToAnimate, item => {
        return item.id === comment.id;
      }) >= 0
    );
  }

  isCommentRemoving(comment) {
    return (
      findIndex(this.state.commentsToRemove, item => {
        return item === comment.id;
      }) >= 0
    );
  }

  componentWillReceiveProps(nextProps) {
    nextProps &&
      nextProps.post &&
      nextProps.post.comments &&
      nextProps.post.comments.comments &&
      nextProps.post.comments.comments.forEach(item => {
        if (
          this.isNewComment(item) &&
          findIndex(this.props.post.comments.comments, oldItem => {
            return oldItem.id === item.id;
          }) === -1
        ) {
          this.setState({
            commentsShown: this.state.commentsShown + 1,
            submitting: false
          });
        }
      });
  }

  showLessAvailable() {
    return (
      this.getLoadedCommentCount() > this.getMinimumShown() &&
      this.state.commentsShown > this.getMinimumShown()
    );
  }

  showLess() {
    this.setState({ commentsShown: this.getMinimumShown() });
  }

  beginEditComment(comment) {
    if (!this.props.isEditingPostOrComment()) {
      this.props.setEditingComment(comment.id);
    }
  }

  cancelEditComment() {
    this.props.setEditingComment('');
  }

  saveEditComment(message, comment) {
    if (message !== '') {
      this.props.onSaveEditComment(comment, message);
      this.props.setEditingComment('');
    }
  }

  beginDeleteComment(postId, comment) {
    this.setState({
      commentsToRemove: fromJS(this.state.commentsToRemove)
        .push(comment.id)
        .toJS()
    });
  }

  deleteComment(postId, comment) {
    this.props.onDeleteComment(postId, comment);
  }

  toggleEmojiPicker() {
    const isEmoji = this.state.showEmojiPicker;
    this.setState({
      showEmojiPicker: !isEmoji
    });
  }

  addEmoji(emoji) {
    const { message } = this.state;
    const text = `${message}${emoji.native}`;

    this.setState({
      message: text
    });
  }

  setActiveCommentSelected(id) {
    this.setState({
      activeCommentSelected: id
    });
  }

  render() {
    const { post, commentInputShown, isEditingPostOrComment, showLegacyFeed } =
      this.props;
    const { commentsShown, message, showEmojiPicker } = this.state;

    const sortedComments =
      post && post.comments && post.comments.comments && !post.isLocked
        ? sortBy(post.comments.comments, { prop: 'created' })
        : [];

    const replyCommentClassName = showLegacyFeed
      ? 'legacy-reply-comment'
      : 'reply-comment';

    return (
      <div className="chat-comment-container">
        <div>
          {(commentInputShown === post.id || this.state.submitting) &&
          !post.isLocked ? (
            <div className={`feed-comment-container ${replyCommentClassName}`}>
              <MessageForm
                onSubmit={this.handleSubmit}
                onChange={this.handleChange}
                onKeyDown={this.handleKeyDown}
                onEmojiPicker={this.toggleEmojiPicker}
                message={message}
                placeholder={MESSAGE_TYPE_COMMENT}
                disabled={this.state.submitting}
                autoFocus
                setCommentInput={this.props.setCommentInput}
                showEmojiPicker={showEmojiPicker}
                addEmoji={this.addEmoji}
              />
            </div>
          ) : (
            ''
          )}
        </div>
        <ul className="feed-comment-list">
          {sortedComments.map((comment, idx) => {
            return idx < commentsShown ? (
              <FadeElement
                className="comment-background"
                key={comment.id}
                onTransitionComplete={() => {
                  this.props.onCommentAnimationComplete(comment);
                }}
                onRemoveComplete={() => {
                  this.deleteComment(post.id, comment);
                }}
                in={!this.isNewComment(comment)}
                removing={this.isCommentRemoving(comment)}
              >
                <Comment
                  comment={comment}
                  post={post}
                  showLegacyFeed={this.props.showLegacyFeed}
                  isEditingPostOrComment={isEditingPostOrComment}
                  onEditComment={this.beginEditComment}
                  editingComment={this.props.editingComment}
                  onCancelEditComment={this.cancelEditComment}
                  onSaveEditComment={this.saveEditComment}
                  onDeleteComment={this.beginDeleteComment}
                  user={this.props.user}
                  onLikeComment={this.props.onLikeComment}
                  onUnlikeComment={this.props.onUnlikeComment}
                  openReply={this.props.openReply}
                  setActiveCommentSelected={this.setActiveCommentSelected}
                  activeCommentSelected={this.state.activeCommentSelected}
                />
              </FadeElement>
            ) : null;
          })}
        </ul>
        {(this.showLessAvailable() || this.showMoreAvailable()) &&
          !post.isLocked && (
            <div className={`load-more${post.isPinned ? ' pinned' : ''}`}>
              {this.showMoreAvailable() && (
                <TextButton onClick={this.showMore}>
                  <A11yFontIcon icon="arrow-down" alt="" /> More Comments…
                </TextButton>
              )}
              {this.showLessAvailable() && (
                <TextButton onClick={this.showLess}>
                  Less Comments… <A11yFontIcon icon="arrow-up" alt="" />
                </TextButton>
              )}
            </div>
          )}
      </div>
    );
  }
}

CommentList.propTypes = {
  post: wallPostShape,
  comments: PropTypes.arrayOf(wallCommentShape),
  commentInputShown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  user: userShape,
  setEditingComment: PropTypes.func,
  isEditingPostOrComment: PropTypes.func,
  onSaveEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  addComment: PropTypes.func,
  commentsToAnimate: PropTypes.array,
  onCommentAnimationComplete: PropTypes.func,
  onLikeComment: PropTypes.func,
  onUnlikeComment: PropTypes.func,
  openReply: PropTypes.func,
  setCommentInput: PropTypes.func,
  showLegacyFeed: PropTypes.bool
};

export default CommentList;
